import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../utils/change_time_format";
import { Avatar, Button, IconButton, Modal, Typography } from "@mui/material";
import { bootcampPaymentAction } from "../../../actions/BootCamp/bootcampActions";
import {
  generateMerchantUserId,
  generateTransactionId,
} from "../../../utils/payment_utils";
import YouTube from "react-youtube";
import ReadMoreLess from "../reusables/ReadMoreLess";
import { profileImages } from "../../../utils/constants";
import GoogleMeetIcon from "@mui/icons-material/Videocam";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

function BootcampCard({ item, onClick }) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);

  const media = item?.media?.length > 0 ? item?.media : null;

  const startDate = new Date(
    item?.dates?.startDate ?? "2025-03-02T00:00:00.000Z"
  );
  var formattedStartDate = formatDate(startDate);

  // Calculate current day offset relative to startDate
  const currentDate = new Date();
  const date = formatDate(item?.dates?.startDate ?? "2025-03-02T00:00:00.000Z");
  const timeDiff = currentDate.getTime() - startDate.getTime();
  const dayOffset = Math.floor(timeDiff / (1000 * 3600 * 24)); // Days difference
  const currentBootcampDay = dayOffset >= 0 ? dayOffset + 1 : 1;
  const dateToDisplay = dayOffset >= 0 ? currentDate : startDate;
  formattedStartDate = formatDate(dateToDisplay);
  const alreadyRegistered = item?.registeredUsers?.includes(userInfo?._id);
  const isPaidUser = item?.paidUsers?.includes(userInfo?._id);
  const hasUserSelectedTimeSlot = item?.timeSlots?.find((slot) =>
    slot.registeredUsers.includes(userInfo?._id)
  );
  var timeSlotDetails = item?.timeSlots?.find((slot) =>
    slot?.registeredUsers?.includes(userInfo?._id)
  );
  const { confirmTimeSlot } = useSelector(
    (state) => state.confirmTimeSlotStore
  );
  if (
    timeSlotDetails === undefined ||
    (timeSlotDetails === null && confirmTimeSlot?.timeSlot)
  ) {
    timeSlotDetails = {
      title: confirmTimeSlot?.timeSlot?.title,
      startTime: confirmTimeSlot?.timeSlot?.startTime,
      endTime: confirmTimeSlot?.timeSlot?.endTime,
    };
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const googleMeetLink =
    item?.meetLink?.length > 0
      ? item?.meetLink
      : "https://meet.google.com/abc-defg-hij";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(googleMeetLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleJoinClick = () => {
    window.open(googleMeetLink, "_blank");
  };

  const handleLiveButtonClick = (e) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const sessionDetails = {
    capacity: item?.capacity || 35,
    endTime: timeSlotDetails?.endTime || "12:30 PM",
    registeredUsers: item?.registeredUsers || ["66067ac230d0a9ac1e70692e"],
    startTime: timeSlotDetails?.startTime || "10:00 AM",
    title: item?.title || "Morning Theory Session",
    _id: item?._id || "67c35eaaa5bc60b7b4d17b18",
  };

  const isSessionLive = () => {
    if (
      !timeSlotDetails ||
      !timeSlotDetails.startTime ||
      !timeSlotDetails.endTime ||
      !item?.dates?.startDate ||
      !item?.dates?.endDate
    ) {
      return false;
    }
    const now = new Date();

    // Parse startTime
    const startMatch = timeSlotDetails.startTime.match(
      /(\d+):(\d+)\s*(AM|PM)/i
    );
    if (!startMatch) return false;
    const [, startHours, startMinutes, startPeriod] = startMatch;
    let startHour = parseInt(startHours);
    const startMinute = parseInt(startMinutes);
    if (startPeriod.toUpperCase() === "PM" && startHour !== 12) startHour += 12;
    if (startPeriod.toUpperCase() === "AM" && startHour === 12) startHour = 0;

    // Parse endTime
    const endMatch = timeSlotDetails.endTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!endMatch) return false;
    const [, endHours, endMinutes, endPeriod] = endMatch;
    let endHour = parseInt(endHours);
    const endMinute = parseInt(endMinutes);
    if (endPeriod.toUpperCase() === "PM" && endHour !== 12) endHour += 12;
    if (endPeriod.toUpperCase() === "AM" && endHour === 12) endHour = 0;

    // Define session date range
    const sessionStartDate = new Date(item.dates.startDate);
    sessionStartDate.setHours(0, 0, 0, 0); // Start of first day

    const sessionEndDate = new Date(item.dates.endDate);
    sessionEndDate.setHours(23, 59, 59, 999); // End of last day

    // Check if now is within the date range
    const nowDateOnly = new Date(now);
    nowDateOnly.setHours(0, 0, 0, 0);
    if (nowDateOnly < sessionStartDate || nowDateOnly > sessionEndDate) {
      return false;
    }

    // Apply daily time slot to current day
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const dailyStart = new Date(today);
    dailyStart.setHours(startHour, startMinute, 0, 0);

    const dailyEnd = new Date(today);
    dailyEnd.setHours(endHour, endMinute, 0, 0);

    if (dailyEnd <= dailyStart) {
      dailyEnd.setDate(dailyEnd.getDate() + 1);
    }

    return now >= dailyStart && now <= dailyEnd;
  };

  const liveStatus = isSessionLive();

  const getButtonConfig = (
    isPaidUser,
    alreadyRegistered,
    hasUserSelectedTimeSlot,
    timeSlotDetails
  ) => {
    if (isPaidUser) {
      return {
        text: "All Unlocked",
        className: "book-time-btn full-width unlocked", // Optional: Add a special class for styling
      };
    }

    if (alreadyRegistered) {
      if (hasUserSelectedTimeSlot || timeSlotDetails?.startTime) {
        return {
          text: "View Details",
          className: "book-time-btn full-width",
        };
      }
      return {
        text: "Book Your Time Slot",
        className: "book-time-btn full-width",
      };
    }

    // Default case (if not registered and not a paid user)
    return {
      text: "Book Your Time Slot",
      className: "book-time-btn full-width",
    };
  };

  const buttonConfig = getButtonConfig(
    isPaidUser,
    alreadyRegistered,
    hasUserSelectedTimeSlot,
    timeSlotDetails
  );
  // Check if the bootcamp is in the past
  // const currentDate = new Date();
  const endDate = new Date(item?.dates?.endDate);
  const isPastBootcamp = endDate < currentDate;
  const isDay1Free = item?.daywiseDetails?.[0]?.isFree || false;
  const currentDayIndex = item?.daywiseDetails?.findIndex((day) => {
    const dayDate = new Date(item.dates.startDate);
    dayDate.setDate(dayDate.getDate() + item.daywiseDetails.indexOf(day));
    return dayDate.toDateString() === new Date().toDateString();
  });
  const isBeyondDay1 = currentDayIndex > 0;

  const renderModalContent = () => {
    if (isDay1Free && isBeyondDay1 && !isPaidUser) {
      // Show payment prompt for non-paid users on Day 2+ after free Day 1
      return (
        <div className="ai-modal-payment-prompt">
          <Typography
            variant="h6"
            sx={{ color: "#fff", mb: 2, fontWeight: "bold" }}
          >
            Continue Your Learning Journey
          </Typography>
          <Typography sx={{ color: "#d6deeb", mb: 2 }}>
            You’ve enjoyed Day 1 for free! To access{" "}
            <strong> All Upcoming Days</strong>, unlock the full bootcamp with a
            one-time payment.
          </Typography>
          <Button
            variant="contained"
            className="workshop-button purple animated-pay-button"
            onClick={() =>
              dispatch(
                bootcampPaymentAction(
                  generateTransactionId(),
                  generateMerchantUserId(userInfo?._id),
                  item?.price?.discountPrice,
                  null,
                  item?._id
                )
              )
            }
          >
            Unlock All Days for ₹{item?.price?.discountPrice}
          </Button>
          <Typography className="original-price" sx={{ mt: 1 }}>
            <s>₹{item?.price?.originalPrice}</s> - One-time fee for full access!
          </Typography>
        </div>
      );
    } else {
      // Default content for Day 1 or non-paid users before Day 2
      return (
        <>
          <div className="ai-modal-details">
            <div className="time-info">
              <div
                style={{
                  marginRight: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CalendarMonthIcon
                  sx={{ fontSize: 18, mr: 1, color: "#4CAF50" }}
                />
                <span>
                  {formattedStartDate?.day} {formattedStartDate?.month},{" "}
                  {formattedStartDate?.year}
                </span>
              </div>
              <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: "#4CAF50" }} />
              <span>
                {sessionDetails.startTime} - {sessionDetails.endTime}
              </span>
            </div>
            <div className="activation-message">
              <span style={{ fontSize: "12px" }}>
                {liveStatus
                  ? "Link is Activated On:"
                  : "Link will be activated on:"}
              </span>
              <span style={{ fontWeight: "900" }}>
                {formattedStartDate?.day} {formattedStartDate?.month},{" "}
                {formattedStartDate?.year} at {sessionDetails.startTime}
              </span>
            </div>
            <div className="ai-modal-link-container">
              <GoogleMeetIcon sx={{ fontSize: 20, mr: 1, color: "#4285F4" }} />
              {liveStatus ? (
                <a
                  href={googleMeetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ai-modal-link"
                  onClick={(e) => !liveStatus && e.preventDefault()}
                >
                  {googleMeetLink}
                </a>
              ) : (
                <span className="ai-modal-link blurred-link">
                  Link available when live
                </span>
              )}
              {liveStatus && (
                <IconButton
                  onClick={handleCopyLink}
                  sx={{ ml: 1 }}
                  aria-label="Copy link"
                >
                  {isCopied ? (
                    <CheckIcon sx={{ color: "#4CAF50" }} />
                  ) : (
                    <ContentCopyIcon sx={{ color: "#b0b0cc" }} />
                  )}
                </IconButton>
              )}
            </div>
          </div>
          <button
            className="ai-modal-join-btn"
            onClick={handleJoinClick}
            disabled={!liveStatus}
          >
            Join Live Session
          </button>
        </>
      );
    }
  };

  return (
    <div
      onClick={() => onClick(item)}
      key={item?._id}
      className={`workshop-card ${isPaidUser ? "premium-card" : ""}`}
    >
      <div className="card-image-container">
        {media?.length > 0 && media[0]?.type === "video" ? (
          <div className="card-workshop-youtube-container">
            <YouTube
              videoId={media[0]?.url}
              opts={{
                playerVars: {
                  autoplay: 0,
                },
              }}
            />
          </div>
        ) : (
          <img
            width={320}
            height={200}
            src={
              (item?.media?.length > 0 && item?.media[0]?.url) ||
              "https://plus.unsplash.com/premium_photo-1677094310919-d0361465d3be?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            }
            alt=""
            className="workshop-image"
          />
        )}
        <div className="date-overlay">
          <CalendarMonthIcon className="calendar-icon" />
          <span className="date-text">
            {date?.day} {date?.month}, {date?.year}
          </span>
        </div>
        {alreadyRegistered && (
          <div
            className={`enrolled-badge ${isPaidUser ? "premium-badge" : ""}`}
          >
            <CheckCircleIcon sx={{ fontSize: 16, marginRight: "4px" }} />
            {isPaidUser ? "Premium" : "Enrolled"}
          </div>
        )}
      </div>
      <div className="card-header">
        <div className="level-tag">{item?.level}</div>
        <div className="spots-info">
          <GroupsIcon className="groups-icon" />
          <span className="spots-text">
            {item?.spots?.available} Spots Left
          </span>
        </div>
      </div>
      <h3 className="workshop-title">{item?.title}</h3>
      <p className="workshop-desc">
        <ReadMoreLess color={"#fff"} text={item?.description} maxLength={60} />
      </p>
      <div className="registered-preview">
        {profileImages.slice(0, 3).map((item, idx) => (
          <Avatar
            key={idx}
            sx={{ width: "25px", height: "25px" }}
            src={item}
            className="avatar-overlap"
          />
        ))}
        <span className="registered-count">
          +
          {item?.registeredUsers?.length >= 20
            ? item?.registeredUsers?.length
            : 20}{" "}
          Enrolled
        </span>
      </div>
      <div className="card-footer">
        {isPastBootcamp ? (
          <button className="completed-btn full-width" disabled>
            <CheckCircleIcon sx={{ fontSize: 16, marginRight: "4px" }} />
            Completed
          </button>
        ) : alreadyRegistered ? (
          <button
            className={buttonConfig.className}
            onClick={() => onClick(item)}
          >
            {isPaidUser && (
              <img
                style={{ width: "15px", height: "15px", marginRight: "5px" }}
                src="images/lock_open_black_icon.png"
                alt="open lock icon"
              />
            )}
            {buttonConfig.text}
          </button>
        ) : (
          <button className="select-time-btn" onClick={() => onClick(item)}>
            Select Time Slot
          </button>
        )}
        {!isPastBootcamp &&
          (hasUserSelectedTimeSlot || timeSlotDetails?.startTime) && (
            <button
              className="live-session-btn"
              onClick={handleLiveButtonClick}
            >
              <span className="live-dot" />
              Join Now
            </button>
          )}
      </div>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="live-session-modal-title"
        sx={{
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(3px)",
            background: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <div onClick={(e) => e.stopPropagation()} className="ai-modal">
          <div className="ai-modal-header">
            <span className="ai-modal-title">
              {isDay1Free && isBeyondDay1
                ? item?.title
                : timeSlotDetails?.title || "Selected Session"}
            </span>
            <IconButton
              className="ai-modal-close"
              onClick={() => setModalOpen(false)}
            >
              <CloseIcon style={{ color: "#fff" }} />
            </IconButton>
          </div>
          <div className="ai-modal-content">{renderModalContent()}</div>
        </div>
      </Modal>
    </div>
  );
}

export default BootcampCard;
